import { useEffect, useMemo, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ImagePlus, Loader2, ChevronLeft, Crop } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { processSelectedFiles, IMAGE_ACCEPT, resetFileInput } from '@/lib/photoInputUtils';
import { BulkPhotoEditorSheet } from './BulkPhotoEditorSheet';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import type { UserItem } from '@/types/userItem';
import { activity } from '@/store/activityStore';

const PHOTO_ACTIVITY_ID = 'bulk-photo-upload';
import { useQueryClient } from '@tanstack/react-query';
import { useBulkMatchQueue, type MatchedItem } from './useBulkMatchQueue';
import { BulkMatchQueueBody } from './BulkMatchQueueBody';
import { useUploadQueue } from '@/hooks/useUploadQueue';
import { BulkUploadStatusStrip } from './BulkUploadStatusStrip';
import { BulkPhotoTriage, type TriagePair } from './BulkPhotoTriage';
import {
  processBulkUploadPair,
  type BulkUploadResult,
  type BulkUploadPairData,
  type BulkPhotoBatchType,
} from '@/lib/bulkPhotoUpload';

type Step = 'pick' | 'triage' | 'match';
type PairMode = 'pairs' | 'singles';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired with only the newly created drafts, so the caller can open Rapid List. */
  onCreated?: (items: UserItem[]) => void;
}

const MAX_PHOTOS = 500;

export function BulkPhotoUploadSheet({ open, onOpenChange, onCreated }: Props) {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const kbInset = useVisualViewportInset();

  const [step, setStep] = useState<Step>('pick');
  const [batchType, setBatchType] = useState<BulkPhotoBatchType>('singles');
  const [files, setFiles] = useState<File[]>([]);
  const [pairMode, setPairMode] = useState<PairMode>('pairs');
  const [picking, setPicking] = useState<{ done: number; total: number; phase: 'reading' | 'dng' | 'heic' } | null>(null);
  const [awaitingPicker, setAwaitingPicker] = useState(false);
  const [triagePairs, setTriagePairs] = useState<TriagePair[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);


  const completedRef = useRef<Map<string, BulkUploadResult>>(new Map());
  // Drafts land here the moment their DB row is inserted — well before their
  // photo bytes finish uploading. Lets us hand off to the pricing queue with
  // zero wait even when uploads are still in flight.
  const earlyDraftsRef = useRef<Map<string, UserItem>>(new Map());
  const matchedPairsRef = useRef<Set<string>>(new Set());
  const hasInvalidatedRef = useRef(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { jobs, stats, add, retryAll } = useUploadQueue<BulkUploadPairData, BulkUploadResult>({
    concurrency: 3,
    maxRetries: 2,
    processor: processBulkUploadPair,
  });

  // Reset everything when the sheet closes so the next session starts fresh.
  useEffect(() => {
    if (!open) {
      setStep('pick');
      setFiles([]);
      setBatchType('singles');
      setPairMode('pairs');
      setPicking(null);
      setAwaitingPicker(false);
      setEditorOpen(false);
      setTriagePairs([]);
      completedRef.current = new Map();
      earlyDraftsRef.current = new Map();
      matchedPairsRef.current = new Set();
      hasInvalidatedRef.current = false;

    }
  }, [open]);

  // When the OS file picker closes and returns focus without a change event,
  // clear the "getting your photos" overlay so it doesn't hang if the user
  // cancelled out of the iOS Photos sheet.
  useEffect(() => {
    if (!awaitingPicker) return;
    const clear = () => {
      // Give the change event a moment to fire first — otherwise focus can
      // race ahead and hide the overlay right before the pipeline kicks in.
      setTimeout(() => setAwaitingPicker(false), 400);
    };
    window.addEventListener('focus', clear);
    return () => window.removeEventListener('focus', clear);
  }, [awaitingPicker]);


  // Track which pair keys are currently in-flight so we don't double-submit.
  const activePairKeys = useMemo(() => {
    const set = new Set<string>();
    for (const j of jobs) {
      if (j.status === 'queued' || j.status === 'uploading' || j.status === 'retrying') {
        set.add(j.data.pairKey);
      }
    }
    return set;
  }, [jobs]);

  // Record completed results as jobs finish.
  useEffect(() => {
    for (const job of jobs) {
      if (job.status === 'done' && !completedRef.current.has(job.data.pairKey)) {
        completedRef.current.set(job.data.pairKey, job.result ?? { mode: 'draft' });
      }
    }
  }, [jobs]);

  // Tier 3: keep the thin global bar alive while uploads run, so closing this
  // sheet doesn't make the work look like it stopped.
  useEffect(() => {
    const active = stats.uploading + stats.retrying + stats.queued;
    if (active > 0) {
      activity.start({
        id: PHOTO_ACTIVITY_ID,
        label: 'Uploading photos',
        done: stats.done + stats.failed,
        total: stats.total,
        failed: stats.failed,
      });
    } else {
      activity.end(PHOTO_ACTIVITY_ID);
    }
  }, [stats]);

  useEffect(() => () => activity.end(PHOTO_ACTIVITY_ID), []);

  // Invalidate inventory once the queue quiets down and something finished.
  useEffect(() => {
    const active = stats.uploading + stats.retrying + stats.queued;
    if (active === 0 && stats.done > 0 && !hasInvalidatedRef.current) {
      hasInvalidatedRef.current = true;
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
    }
  }, [stats, queryClient]);

  const handlePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const snapshot = e.target.files ? Array.from(e.target.files) : [];
    resetFileInput(e.currentTarget);
    setAwaitingPicker(false);
    if (snapshot.length === 0) return;
    setPicking({ done: 0, total: snapshot.length, phase: 'reading' });
    try {
      const picked = await processSelectedFiles(snapshot, {
        maxCount: MAX_PHOTOS - files.length,
        onProgress: (done, total, phase) => setPicking({ done, total, phase }),
      });
      if (picked.length === 0) return;
      setFiles((prev) => [...prev, ...picked].slice(0, MAX_PHOTOS));
    } finally {
      setPicking(null);
    }
  };


  const goForward = () => {
    if (pairMode === 'pairs') setStep('triage');
    else setStep('match');
  };

  const matchPairs = useMemo(
    () => triagePairs.map((p) => ({ key: p.key, front: p.front, back: p.back })),
    [triagePairs]
  );
  const matchQueue = useBulkMatchQueue(matchPairs, open && step === 'match');

  const advanceAndUpload = (
    override?: { kind: 'matched'; item: MatchedItem } | { kind: 'draft' } | { kind: 'skip' },
  ) => {
    const current = matchQueue.current;
    if (!current) return;
    // Prefer the explicit decision passed in from the click handler so we don't
    // race React state (the recorded decision from matchQueue.attach/draft isn't
    // visible in this closure yet).
    const d = override ?? matchQueue.decisions.get(current.key);

    if (d?.kind === 'skip') {
      matchQueue.skip();
      return;
    }

    const resolution =
      d?.kind === 'matched'
        ? ({ mode: 'matched' as const, item: d.item })
        : ({ mode: 'draft' as const });

    const alreadyDone = completedRef.current.get(current.key);
    if (alreadyDone) {
      // Photos are already committed to the backend — a second decision would
      // silently no-op. Warn the user instead of pretending it worked.
      const changed =
        (alreadyDone.mode === 'matched' && resolution.mode !== 'matched') ||
        (alreadyDone.mode === 'draft' && resolution.mode === 'matched') ||
        (alreadyDone.mode === 'matched' &&
          resolution.mode === 'matched' &&
          alreadyDone.item?.id !== resolution.item.id);
      if (changed) {
        toast.warning('This pair was already uploaded — reopen the item in Inventory to reassign.');
      }
    } else if (!activePairKeys.has(current.key)) {
      if (resolution.mode === 'matched') matchedPairsRef.current.add(current.key);
      const pairKey = current.key;
      add({
        pairKey,
        files: [current.front, current.back].filter(Boolean) as File[],
        resolution,
        batchType,
        onDraftCreated: (item) => {
          earlyDraftsRef.current.set(pairKey, item);
        },
      });
    }

    if (d?.kind === 'matched') matchQueue.attach(d.item);
    else matchQueue.draft();
  };



  const [finishing, setFinishing] = useState(false);

  const finish = async () => {
    // Draft rows are inserted inside the upload processor before any photo
    // bytes are pushed, so we don't have to wait on the full upload pipeline
    // any more. We only briefly wait for any *just-queued* pair to reach the
    // point where its row exists (usually <500ms), then hand off.
    const expectedDraftPairs = new Set<string>();
    for (const j of jobs) {
      const key = j.data.pairKey;
      if (j.data.resolution.mode === 'draft' && !matchedPairsRef.current.has(key)) {
        expectedDraftPairs.add(key);
      }
    }
    const missingDraftRows = () => {
      for (const k of expectedDraftPairs) {
        if (!earlyDraftsRef.current.has(k)) return true;
      }
      return false;
    };
    if (missingDraftRows()) {
      setFinishing(true);
      await new Promise<void>((resolve) => {
        const t = setInterval(() => {
          if (!missingDraftRows()) {
            clearInterval(t);
            resolve();
          }
        }, 100);
        // Short safety cap — row insert is one round trip; if it hasn't landed
        // in 3s something else is wrong and we still hand off with what we have.
        setTimeout(() => {
          clearInterval(t);
          resolve();
        }, 3_000);
      });
      setFinishing(false);
    }

    const createdItems: UserItem[] = Array.from(earlyDraftsRef.current.values());
    const created = createdItems.length;
    const attached = matchedPairsRef.current.size;
    const failed = stats.failed;
    const bits: string[] = [];
    if (attached > 0) bits.push(`Attached to ${attached} item${attached === 1 ? '' : 's'}`);
    if (created > 0) bits.push(`Created ${created} draft${created === 1 ? '' : 's'}`);
    const stillUploading = stats.uploading + stats.queued + stats.retrying;
    if (stillUploading > 0) bits.push(`${stillUploading} photo${stillUploading === 1 ? '' : 's'} still uploading in the background`);
    const summary = bits.join(' · ') || 'Upload complete';
    if (failed > 0) toast.warning(`${summary} · ${failed} failed`);
    else if (created > 0) toast.success(`${summary} — finish pricing next`);
    else toast.success(summary);
    if (createdItems.length > 0) onCreated?.(createdItems);
    onOpenChange(false);
  };


  // Keyboard shortcuts for the match queue.
  useEffect(() => {
    const inQueue = step === 'match' && !!matchQueue.current;
    if (!inQueue) return;
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      const tag = tgt?.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tgt?.isContentEditable;
      if (e.key === 'Enter' && !e.shiftKey && !isTyping) {
        e.preventDefault();
        advanceAndUpload();
      } else if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        const prev = matchQueue.index - 1;
        const prevKey = matchPairs[prev]?.key;
        if (prevKey && (completedRef.current.has(prevKey) || activePairKeys.has(prevKey))) return;
        matchQueue.back();

      } else if ((e.key === 's' || e.key === 'S') && !isTyping) {
        e.preventDefault();
        matchQueue.skip();
      } else if ((e.key === 'd' || e.key === 'D') && !isTyping) {
        e.preventDefault();
        matchQueue.draft();
      } else if ((e.key === 'f' || e.key === 'F') && !isTyping) {
        e.preventDefault();
        matchQueue.swap();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, matchQueue, advanceAndUpload]);

  const pickBody = (
    <div className="flex flex-col gap-5">
      {/* Type */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</span>
        <div className="grid grid-cols-2 gap-2">
          {(['singles', 'slabs'] as BulkPhotoBatchType[]).map((t) => (
            <button
              key={t}
              type="button"
              disabled={!!picking}
              onClick={() => setBatchType(t)}
              className={cn(
                'h-10 rounded-full text-sm font-medium border transition',
                batchType === t
                  ? 'bg-active-gold text-black border-active-gold'
                  : 'bg-white/[0.04] text-foreground/80 border-white/[0.08] hover:bg-white/[0.08]'
              )}
            >
              {t === 'singles' ? 'Singles' : 'Slabs'}
            </button>
          ))}
        </div>
      </div>

      {/* Grouping */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Grouping</span>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={!!picking}
            onClick={() => setPairMode('pairs')}
            className={cn(
              'h-10 rounded-full text-sm font-medium border transition',
              pairMode === 'pairs'
                ? 'bg-active-gold/10 text-active-gold border-active-gold/40'
                : 'bg-white/[0.04] text-foreground/70 border-white/[0.08] hover:bg-white/[0.08]'
            )}
          >
            Front + back
          </button>
          <button
            type="button"
            disabled={!!picking}
            onClick={() => setPairMode('singles')}
            className={cn(
              'h-10 rounded-full text-sm font-medium border transition',
              pairMode === 'singles'
                ? 'bg-active-gold/10 text-active-gold border-active-gold/40'
                : 'bg-white/[0.04] text-foreground/70 border-white/[0.08] hover:bg-white/[0.08]'
            )}
          >
            One per item
          </button>
        </div>
      </div>

      {/* Picker */}
      <div>
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          multiple
          onChange={handlePick}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          disabled={!!picking || files.length >= MAX_PHOTOS}
          onClick={() => {
            setAwaitingPicker(true);
            inputRef.current?.click();
          }}
          className="w-full h-12 gap-2 rounded-full bg-white/[0.05] border-white/[0.08] hover:bg-white/[0.10] text-base"
        >
          {picking ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-active-gold" />
              {picking.phase === 'reading'
                ? `Reading ${picking.total}…`
                : `Processing ${picking.done}/${picking.total}…`}
            </>
          ) : (
            <>
              <ImagePlus className="w-5 h-5 text-active-gold" />
              {files.length === 0 ? 'Choose photos' : `Add more (${files.length}/${MAX_PHOTOS})`}
            </>
          )}
        </Button>

      </div>

      {files.length > 0 && (
        <>
          <div className="text-xs text-muted-foreground tabular-nums">
            {files.length} photo{files.length === 1 ? '' : 's'} selected
            {pairMode === 'pairs' && (
              <span className="ml-1">· review pairing on the next step</span>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={!!picking}
            onClick={() => setEditorOpen(true)}
            className="w-full h-10 gap-2 rounded-full bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08] text-sm"
          >
            <Crop className="w-4 h-4 text-active-gold" />
            Crop &amp; rotate photos
          </Button>
        </>
      )}
    </div>
  );


  const triageBody = (
    <div className="flex flex-col gap-4 min-h-0">
      <div className="text-xs text-muted-foreground leading-snug">
        Tap two singles to pair them, or split a pair if the front/back order is wrong. Swipe/scroll through all
        photos before continuing.
      </div>
      <BulkPhotoTriage photos={files} initialMode={pairMode} onChange={setTriagePairs} />
    </div>
  );

  const matchBody = (
    <div className="flex flex-col gap-3 min-h-0 flex-1">
      <BulkUploadStatusStrip jobs={jobs} onRetryAll={retryAll} />
      {matchQueue.current ? (
        <BulkMatchQueueBody
          pair={matchQueue.current}
          index={matchQueue.index}
          total={matchQueue.total}
          decision={matchQueue.decisions.get(matchQueue.current.key)}
          onAttach={(item) => advanceAndUpload({ kind: 'matched', item })}
          onDraft={() => advanceAndUpload({ kind: 'draft' })}
          onSkip={() => advanceAndUpload({ kind: 'skip' })}
          onSwap={() => matchQueue.swap()}
        />
      ) : (
        <div className="flex flex-col gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 text-center">
          <div className="text-sm font-medium text-foreground">All caught up</div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="rounded-lg bg-white/[0.03] p-2">
              <div className="text-active-gold font-semibold">{matchQueue.summary.attaching}</div>
              <div className="text-muted-foreground">Attached</div>
            </div>
            <div className="rounded-lg bg-white/[0.03] p-2">
              <div className="text-active-gold font-semibold">{matchQueue.summary.draft}</div>
              <div className="text-muted-foreground">Drafts</div>
            </div>
            <div className="rounded-lg bg-white/[0.03] p-2">
              <div className="text-active-gold font-semibold">{matchQueue.summary.skipped}</div>
              <div className="text-muted-foreground">Skipped</div>
            </div>
          </div>
          {stats.failed > 0 && (
            <div className="text-xs text-destructive">
              {stats.failed} upload{stats.failed === 1 ? '' : 's'} failed — retry from the status bar above.
            </div>
          )}
        </div>
      )}
    </div>
  );

  const inQueue = step === 'match' && !!matchQueue.current;

  const footer = (
    <div
      className="pt-3 mt-2 border-t border-white/[0.06] flex items-center gap-2"
      style={
        isMobile
          ? { paddingBottom: `calc(env(safe-area-inset-bottom) + 20px + ${kbInset}px)` }
          : undefined
      }
    >

      {step === 'pick' && (
        <Button
          type="button"
          variant="ghost"
          disabled={!!picking}
          onClick={() => onOpenChange(false)}
          className="rounded-full"
        >
          Cancel
        </Button>
      )}
      {step === 'triage' && (
        <Button
          type="button"
          variant="ghost"
          disabled={!!picking}
          onClick={() => setStep('pick')}
          className="rounded-full gap-1"
        >
          <ChevronLeft className="w-4 h-4" /> Photos
        </Button>
      )}
      {step === 'match' && (
        <>
          {inQueue && matchQueue.index > 0 ? (
            <Button
              type="button"
              onClick={() => {
                const prev = matchQueue.index - 1;
                const prevKey = matchPairs[prev]?.key;
                if (prevKey && (completedRef.current.has(prevKey) || activePairKeys.has(prevKey))) {
                  toast.info('Previous pair already uploaded — can’t be re-decided here.');
                  return;
                }
                matchQueue.back();
              }}
              variant="ghost"
              className="rounded-full gap-1"
              title="Previous pair (Shift+Enter)"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep(step === 'match' && pairMode === 'pairs' ? 'triage' : 'pick')}
              className="rounded-full gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> {pairMode === 'pairs' ? 'Pairs' : 'Photos'}
            </Button>
          )}

        </>
      )}

      {step === 'pick' ? (
        <Button
          type="button"
          disabled={!!picking || files.length === 0}
          onClick={goForward}
          className="ml-auto rounded-full bg-active-gold text-black hover:bg-active-gold/90 disabled:opacity-40"
        >
          Continue · {files.length} photo{files.length === 1 ? '' : 's'}
        </Button>
      ) : step === 'triage' ? (
        <Button
          type="button"
          disabled={triagePairs.length === 0}
          onClick={() => setStep('match')}
          className="ml-auto rounded-full bg-active-gold text-black hover:bg-active-gold/90 disabled:opacity-40"
        >
          Continue · {triagePairs.length} item{triagePairs.length === 1 ? '' : 's'}
        </Button>
      ) : inQueue ? (
        <Button
          type="button"
          onClick={() => advanceAndUpload()}
          className={cn(
            'ml-auto rounded-full bg-active-gold text-black hover:bg-active-gold/90 min-w-[140px] justify-center',
            isMobile && 'h-12 text-base min-w-[160px]'
          )}
          title="Next (Enter)"
        >
          {matchQueue.index === matchQueue.total - 1 ? 'Finish' : 'Next'}
        </Button>

      ) : (() => {
        const active = stats.uploading + stats.retrying + stats.queued;
        const busy = finishing || active > 0;
        return (
          <Button
            type="button"
            onClick={finish}
            disabled={busy}
            title={active > 0 ? `Waiting for ${active} upload${active === 1 ? '' : 's'} to finish` : undefined}
            className="ml-auto rounded-full bg-active-gold text-black hover:bg-active-gold/90 min-w-[180px] justify-center disabled:opacity-70"
          >
            {busy ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {active > 0 ? `Uploading ${active}…` : 'Finishing…'}
              </span>
            ) : (() => {
              let created = 0;
              for (const r of completedRef.current.values()) if (r.mode === 'draft') created += 1;
              return created > 0 ? `Finish · price ${created} draft${created === 1 ? '' : 's'}` : 'Done';
            })()}
          </Button>
        );
      })()}
    </div>
  );

  const title =
    step === 'pick' ? 'Bulk photo upload' : step === 'triage' ? 'Review pairs' : 'Match to items';
  const body = step === 'pick' ? pickBody : step === 'triage' ? triageBody : matchBody;

  const pickingPct =
    picking && picking.total > 0 && picking.phase !== 'reading'
      ? Math.min(100, Math.round((picking.done / picking.total) * 100))
      : 0;

  const pickingOverlay = picking ? (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-[280px] rounded-2xl border border-active-gold/30 bg-chrome p-5 shadow-2xl flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-active-gold" />
          <span className="text-sm font-medium text-foreground">
            {picking.phase === 'reading'
              ? 'Reading photos…'
              : picking.phase === 'heic'
              ? 'Converting HEIC…'
              : 'Preparing photos…'}
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full bg-active-gold transition-[width] duration-200 ease-out"
            style={{ width: `${pickingPct || 6}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground tabular-nums">
          <span>
            {picking.phase === 'reading'
              ? `${picking.total} photo${picking.total === 1 ? '' : 's'}`
              : `${picking.done} / ${picking.total}`}
          </span>
          <span className="text-active-gold font-semibold">{pickingPct}%</span>
        </div>
        <div className="text-[11px] text-muted-foreground leading-snug">
          Large batches (100+) can take up to a minute. Keep this window open.
        </div>
      </div>
    </div>
  ) : awaitingPicker ? (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-[280px] rounded-2xl border border-active-gold/30 bg-chrome p-5 shadow-2xl flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-active-gold" />
          <span className="text-sm font-medium text-foreground">Getting your photos…</span>
        </div>
        <div className="text-[12px] text-muted-foreground leading-snug">
          iOS is handing your selection over. Large batches (100+ photos) can take 10–20 seconds — hang tight.
        </div>
      </div>
    </div>
  ) : null;


  // Intercept dismissal so drafts created during the session are still handed
  // off to the price queue — otherwise closing via X / backdrop / Esc silently
  // drops the follow-up prompt.
  const handleOpenChange = (o: boolean) => {
    if (picking || finishing) return;
    if (!o && open) {
      const hasDrafts = Array.from(completedRef.current.values()).some(
        (r) => r.mode === 'draft' && r.item,
      );
      if (hasDrafts) {
        void finish();
        return;
      }
    }
    onOpenChange(o);
  };

  const editorMount = (
    <BulkPhotoEditorSheet
      open={editorOpen}
      files={files}
      onClose={() => setEditorOpen(false)}
      onApply={(next) => {
        setFiles(next);
        setEditorOpen(false);
      }}
    />
  );

  if (isMobile) {
    return (
      <>
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <SheetContent
            side="bottom"
            className="bg-chrome border-white/[0.06] rounded-t-2xl p-0 h-[94dvh] flex flex-col"
          >
            <SheetHeader className="px-5 pt-4 pb-3 text-left shrink-0">
              <SheetTitle className="text-base font-semibold">{title}</SheetTitle>
              <SheetDescription className="sr-only">Bulk photo upload</SheetDescription>
            </SheetHeader>
            <div className="px-5 pb-2 overflow-y-auto flex-1 min-h-0">{body}</div>
            <div className="px-5 shrink-0 bg-chrome">{footer}</div>
            {pickingOverlay}
          </SheetContent>
        </Sheet>
        {editorMount}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-2xl sm:w-full sm:h-[85dvh] h-[85dvh] glass-obsidian border-white/[0.06] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">Bulk photo upload</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-2 overflow-y-auto flex-1 min-h-0">{body}</div>
          <div className="px-6 pb-6 shrink-0">{footer}</div>
          {pickingOverlay}
        </DialogContent>
      </Dialog>
      {editorMount}
    </>
  );
}
